package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.arn
import com.siriusxm.cmc.utils.validateArn
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.can
import com.siriusxm.pia.components.*
import com.siriusxm.pia.rest.unifiedaggregator.addTarget
import com.siriusxm.pia.rest.unifiedaggregator.getConsumer
import com.siriusxm.pia.showError
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.backfill.backfillProbe
import com.siriusxm.unifiedcontent.console.aggregator.rememberCapabilities
import smithy4kt.SmithyError
import unifiedcontent.aggregator.*
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * The two modes of target creation: ARN-based (traditional EventBridge/Kinesis targets)
 * and API Destination (HTTP endpoint targets with managed credentials).
 */
internal enum class TargetMode(val label: String, val description: String) {
    ARN("ARN Target", "Deliver to an existing EventBridge bus or Kinesis stream using its ARN"),
    API_DESTINATION("API Destination", "Deliver to an HTTPS endpoint. UCA will create and manage the EventBridge API Destination.")
}

/**
 * The synchronous input checks for the Add Target dialog. Returns the message to show for the
 * first problem found, or null when the input can be submitted.
 *
 * The dialog uses this both to enable the Add button (UCA-602) and again at submit time. It does
 * not cover the ConsumerTargetEdit check for the entered account, which needs an API call and so
 * runs only on submit.
 */
internal fun consumerTargetInputError(
    targetMode: TargetMode,
    targetArn: String?,
    invocationUrl: String?,
    authType: ApiDestinationAuthType,
    basicUsername: String?,
    basicPassword: String?,
    apiKeyName: String?,
    apiKeyValue: String?,
    oauthClientId: String?,
    oauthClientSecret: String?,
    oauthTokenUrl: String?,
    oauthAdditionalParams: List<ApiDestinationOAuthHttpParameter>,
): String? = when (targetMode) {
    TargetMode.ARN ->
        if (targetArn == null || !validateArn(targetArn)) "Please enter a valid ARN" else null

    TargetMode.API_DESTINATION -> {
        val missingCreds = when (authType) {
            ApiDestinationAuthType.Basic ->
                basicUsername.isNullOrBlank() || basicPassword.isNullOrBlank()
            ApiDestinationAuthType.ApiKey ->
                apiKeyName.isNullOrBlank() || apiKeyValue.isNullOrBlank()
            ApiDestinationAuthType.OAuthClientCredentials ->
                oauthClientId.isNullOrBlank() || oauthClientSecret.isNullOrBlank()
                        || oauthTokenUrl.isNullOrBlank()
            else -> true
        }
        when {
            invocationUrl?.startsWith("https://") != true -> "Invocation URL must start with https://"
            missingCreds -> "Please fill in all required credential fields"
            // Each additional OAuth parameter must have both a key and a value
            authType == ApiDestinationAuthType.OAuthClientCredentials &&
                    oauthAdditionalParams.any { it.key.isBlank() || it.value.isBlank() } ->
                "Additional OAuth parameters must have both a key and a value"
            else -> null
        }
    }
}

/**
 * A composable section for editing a dynamic list of OAuth HTTP parameters (headers, body params,
 * or query string params). Uses the standard box component with enableExpanding for
 * collapse/expand behavior.
 *
 * When expanded, each parameter is displayed as a horizontal row with:
 * - A text field for the parameter key (name)
 * - A text field (or password field if isValueSecret is checked) for the parameter value
 * - A checkbox to mark the value as secret (stored in AWS Secrets Manager)
 * - A trash icon button to remove the parameter
 *
 * An "Add" action button in the box header allows adding new empty parameter rows.
 *
 * @param label The display label for this parameter section (e.g., "Header Parameters")
 * @param parameters The current list of parameters
 * @param onUpdate Callback with the updated parameter list whenever a change is made
 */
@Composable
fun OAuthHttpParameterEditor(
    label: String,
    parameters: List<ApiDestinationOAuthHttpParameter>,
    onUpdate: (List<ApiDestinationOAuthHttpParameter>) -> Unit
) {
    box({
        header(builder = {
            title = if (parameters.isNotEmpty()) "$label (${parameters.size})" else label
            titleSize = HeaderSize.SMALL
        })
        enableExpanding(parameters.isNotEmpty())
        action {
            title = "Add"
            this.action {
                onUpdate(parameters + ApiDestinationOAuthHttpParameter(key = "", value = ""))
            }
        }
    }) {
        if (parameters.isEmpty()) {
            boxMessage("No parameters. Click Add to include one.")
        } else {
            parameters.forEachIndexed { index, param ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                        if (index > 0) {
                            marginTop(8.px)
                        }
                    }
                }) {
                    // Key field
                    Div({ style { property("flex", "1") } }) {
                        Input(InputType.Text) {
                            value(param.key)
                            attr("placeholder", "Key")
                            style {
                                width(100.percent)
                                padding(6.px, 8.px)
                                fontSize(13.px)
                                border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
                                property("border-radius", "4px")
                                property("box-sizing", "border-box")
                            }
                            onInput { e ->
                                val updated = parameters.toMutableList()
                                updated[index] = param.copy(key = e.value)
                                onUpdate(updated)
                            }
                        }
                    }

                    // Value field (password if secret, text otherwise)
                    Div({ style { property("flex", "1") } }) {
                        Input(if (param.isValueSecret == true) InputType.Password else InputType.Text) {
                            value(param.value)
                            attr("placeholder", "Value")
                            style {
                                width(100.percent)
                                padding(6.px, 8.px)
                                fontSize(13.px)
                                border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
                                property("border-radius", "4px")
                                property("box-sizing", "border-box")
                            }
                            onInput { e ->
                                val updated = parameters.toMutableList()
                                updated[index] = param.copy(value = e.value)
                                onUpdate(updated)
                            }
                        }
                    }

                    // Secret checkbox
                    simpleCheckBox("Secret", param.isValueSecret ?: false) { checked ->
                        val updated = parameters.toMutableList()
                        updated[index] = param.copy(isValueSecret = checked)
                        onUpdate(updated)
                    }

                    // Remove button
                    svgIcon(SVGIcons.trash) {
                        size = 16.px
                        title = "Remove"
                        action {
                            val updated = parameters.toMutableList()
                            updated.removeAt(index)
                            onUpdate(updated)
                        }
                    }
                }
            }
        }
    }
}

/**
 * Displays the target information for a single ConsumerTarget. For API Destination targets,
 * shows a blue badge, the HTTP method and URL, and a subtitle with region and auth type.
 * For traditional ARN targets, shows the ARN string.
 *
 * @param target The consumer target to display
 */
@Composable
fun consumerTargetDisplay(target: ConsumerTarget) {
    val apiDest = target.apiDestination
    if (apiDest != null) {
        Div {
            Badge("API Destination", BadgeColor.BLUE)
        }
        Div({
            style { marginTop(4.px) }
        }) {
            Text("${apiDest.httpMethod?.name ?: "POST"} ${apiDest.invocationUrl ?: ""}")
        }
        Div({
            style {
                fontSize(12.px)
                color(SXMUI.subtleTextColor.value())
                marginTop(2.px)
            }
        }) {
            Text("Region: ${apiDest.region ?: "unknown"} | Auth: ${apiDest.authType?.name ?: "unknown"}")
        }
    } else {
        Text(target.arn ?: "")
    }
}

/**
 * Displays a list of consumer targets in a table with editing capabilities.
 * Includes an "Add" button that opens an overlay dialog for creating new targets,
 * and delete buttons on each row with confirmation dialogs. Also includes a
 * "Start Backfill" action button for backfill-type targets when the user
 * is allowed by AVP (UnifiedContentAggregator_Backfill, checked per target).
 *
 * Targets are displayed with type, target info (ARN or API Destination details),
 * role, and a delete action column. API Destination targets show a blue badge,
 * the HTTP method/URL, and region/auth metadata.
 *
 * @param aggregator The aggregator service for API calls and navigation
 * @param initialConsumer The consumer whose targets to display
 * @param editable Whether the user may add targets (removal is checked per target)
 * @param onUpdate Callback when targets are modified, receives the updated consumer
 */
@Composable
fun consumerTargetsView(
    aggregator: AggregatorService,
    initialConsumer: ConsumerDetails,
    editable: Boolean,
    onUpdate: suspend (ConsumerDetails) -> Unit
) {
    var consumer by remember { mutableStateOf(initialConsumer) }
    var addingTarget by remember { mutableStateOf(false) }
    var targetPendingDeletion by remember { mutableStateOf<ConsumerTarget?>(null) }
    // Ask AVP, once for the whole page, whether the user may start a backfill against each of this
    // consumer's backfill targets. Hide-only: the play button appears only for targets that come back
    // ALLOW, and the aggregator still authorizes the request itself (403/503 on a rejected start).
    // Legacy admin-group and authorized-agent membership no longer enable the control.
    val startProbes = consumer.targets.orEmpty()
        .filter { it.type == TargetType.Backfill && it.arn != null }
        .associateBy({ it.id }) { backfillProbe(it.arn!!, consumer.id) }
    val startDecisions by aggregator.context.rememberCapabilities(startProbes.values)
    // One ConsumerTargetEdit question per stored target, with the context the API derives from that
    // target on removal (UCA-1240). Hide-only: the trash icon appears only for targets that come back
    // ALLOW. Admin-group and authorized-agent membership no longer enable it.
    val removeProbes = consumer.targets.orEmpty().associateBy({ it.id }) { consumerTargetRemoveProbe(consumer.id, it) }
    val removeDecisions by aggregator.context.rememberCapabilities(removeProbes.values)


    suspend fun deleteTarget(targetId: String) {
        try {
            aggregator.clientApi.removeConsumerTarget(consumer.id, targetId)
            consumer = aggregator.clientApi.getConsumer(consumer.id)
            onUpdate(consumer)
        } catch (e: Throwable) {
            aggregator.context.showError(targetWriteFailure("delete", e), e)
        }
    }

    box({
        title = "Targets"
        paddedContent = false

        if (editable) {
            action {
                title = "Add"
                enabled = !addingTarget
                this.action {
                    addingTarget = true
                }
            }
        }
    }) {
        val hasTargets = !consumer.targets.isNullOrEmpty()
        if (!addingTarget && !hasTargets) {
            boxMessage("This consumer has no targets.")
        }
        if (hasTargets) {
            table<ConsumerTarget> {
                items(consumer.targets.orEmpty())

                column {
                    title = "Type"
                    width = 75.px
                    content { target ->
                        Text(target.type?.name ?: TargetType.Events.name)
                    }
                }
                column {
                    title = "Target"
                    content { target ->
                        consumerTargetDisplay(target)
                    }
                }
                column {
                    title = "Role"
                    content { target ->
                        target.role?.let {
                            Text(it)
                        }
                    }
                }
                // Action column for starting backfill (only for Backfill targets and authorized users)
                column {
                    width = 20.px
                    content { target ->
                        if (target.type == TargetType.Backfill &&
                            startProbes[target.id]?.let { startDecisions?.get(it) } == true
                        ) {
                            svgIcon(SVGIcons.playerPlay) {
                                title = "Start Backfill"
                                action {
                                    val params = buildString {
                                        append("target=${target.arn}")
                                        target.role?.let { append("&role=$it") }
                                    }
                                    aggregator.navigate("backfill/new?$params")
                                }
                            }
                        }
                    }
                }
                // Delete action column (only for targets AVP allows the viewer to remove)
                column {
                    width = 20.px
                    content { target ->
                        if (removeProbes[target.id]?.let { removeDecisions?.get(it) } == true) {
                            svgIcon(SVGIcons.trash) {
                                title = "Delete"
                                action {
                                    targetPendingDeletion = target
                                }
                            }
                        }
                    }
                }
            }
        }

        targetPendingDeletion?.let { target ->
            val parsedArn = target.arn?.arn()
            val targetLabel = if (target.apiDestination != null) {
                target.apiDestination?.invocationUrl ?: "API Destination"
            } else {
                parsedArn?.resourceName ?: target.arn ?: target.id
            }
            confirmationDialogWithContent(
                title = "Remove Target $targetLabel",
                action = "Remove",
                confirmationText = "remove permanently",
                cb = { confirmed ->
                    if (confirmed) {
                        deleteTarget(target.id)
                    }
                    targetPendingDeletion = null
                }
            ) { _, _ ->
                messageBox(MessageType.WARNING) {
                    Text("This will permanently stop deliveries to this target. This cannot be undone.")
                }
            }
        }
    }

    // Render the add target overlay outside the box so it appears as a full-screen modal
    if (addingTarget) {
        consumerTargetEditor(aggregator, consumer) { updatedConsumer ->
            addingTarget = false
            if (updatedConsumer != null) {
                consumer = updatedConsumer
                onUpdate(updatedConsumer)
            }
        }
    }
}

/**
 * Overlay dialog for adding a new target to a consumer. Supports two target modes:
 *
 * 1. **ARN Target** (traditional): User provides a target ARN (EventBridge bus, Kinesis stream)
 *    and optional role ARN. The ARN is used directly as the delivery target.
 *
 * 2. **API Destination**: User provides an HTTPS invocation URL, AWS region,
 *    HTTP method, and authentication credentials. The backend auto-provisions
 *    an EventBridge Connection and API Destination, storing the resulting ARN.
 *    No ARN or role is needed from the user.
 *
 * For API Destinations, the auth type selection controls which credential fields
 * are displayed:
 * - Basic: username and password
 * - ApiKey: key name and key value
 * - OAuthClientCredentials: client ID, client secret, token URL, and optional scopes
 *
 * Sensitive fields (password, API key value, client secret) use masked password inputs.
 *
 * @param aggregator The aggregator service for API calls and error handling
 * @param consumer The consumer to add the target to
 * @param onComplete Callback with the updated consumer details (null if cancelled)
 */
@Composable
fun consumerTargetEditor(
    aggregator: AggregatorService,
    consumer: ConsumerDetails,
    onComplete: suspend (ConsumerDetails?) -> Unit
) {
    // Shared state
    var targetType: TargetType by remember { mutableStateOf(TargetType.Events) }
    var targetMode by remember { mutableStateOf(TargetMode.ARN) }

    // ARN mode state
    var targetArn by remember { mutableStateOf<String?>(null) }
    var targetRole by remember { mutableStateOf<String?>(null) }

    // API Destination mode state
    var invocationUrl by remember { mutableStateOf<String?>(null) }
    var region by remember { mutableStateOf("us-east-2") }
    var httpMethod: ApiDestinationHttpMethod by remember { mutableStateOf(ApiDestinationHttpMethod.POST) }
    var authType: ApiDestinationAuthType by remember { mutableStateOf(ApiDestinationAuthType.Basic) }

    // Basic auth credentials
    var basicUsername by remember { mutableStateOf<String?>(null) }
    var basicPassword by remember { mutableStateOf<String?>(null) }

    // API Key credentials
    var apiKeyName by remember { mutableStateOf<String?>(null) }
    var apiKeyValue by remember { mutableStateOf<String?>(null) }

    // OAuth credentials
    var oauthClientId by remember { mutableStateOf<String?>(null) }
    var oauthClientSecret by remember { mutableStateOf<String?>(null) }
    var oauthTokenUrl by remember { mutableStateOf<String?>(null) }
    var oauthScopes by remember { mutableStateOf<String?>(null) }

    // Additional OAuth HTTP parameters (sent during OAuth token handshake)
    var oauthHeaderParams by remember { mutableStateOf(listOf<ApiDestinationOAuthHttpParameter>()) }
    var oauthBodyParams by remember { mutableStateOf(listOf<ApiDestinationOAuthHttpParameter>()) }
    var oauthQueryStringParams by remember { mutableStateOf(listOf<ApiDestinationOAuthHttpParameter>()) }

    // Inline validation error displayed inside the overlay
    var validationError by remember { mutableStateOf<String?>(null) }

    fun currentInputError() = consumerTargetInputError(
        targetMode = targetMode,
        targetArn = targetArn,
        invocationUrl = invocationUrl,
        authType = authType,
        basicUsername = basicUsername,
        basicPassword = basicPassword,
        apiKeyName = apiKeyName,
        apiKeyValue = apiKeyValue,
        oauthClientId = oauthClientId,
        oauthClientSecret = oauthClientSecret,
        oauthTokenUrl = oauthTokenUrl,
        oauthAdditionalParams = oauthHeaderParams + oauthBodyParams + oauthQueryStringParams,
    )

    suspend fun addTarget() {
        try {
            // Clear any previous validation error
            validationError = null

            // The Add button is only enabled for valid input, but re-check at submit time
            // against the latest state as a second line of defense.
            currentInputError()?.let {
                validationError = it
                return
            }

            if (targetMode == TargetMode.ARN) {
                // Re-ask AVP with this target's account, the same consumerId/targetAccountId context
                // the API checks (UCA-1240), so a team grant scoped to other accounts is caught here
                // rather than as a 403. The capability cache avoids repeat requests.
                val targetAccountId = targetArn!!.arn()?.accountId
                if (targetAccountId != null && !aggregator.context.can(
                        consumerTargetAddProbes(consumer.id, listOf(targetAccountId)).single()
                    )
                ) {
                    validationError = "You do not have permission to add a target in account '$targetAccountId' " +
                            "for this consumer."
                    return
                }
            }

            // Filter out empty additional OAuth parameters (those with no key/value)
            val filteredHeaderParams = oauthHeaderParams.filter { it.key.isNotBlank() && it.value.isNotBlank() }
            val filteredBodyParams = oauthBodyParams.filter { it.key.isNotBlank() && it.value.isNotBlank() }
            val filteredQueryStringParams = oauthQueryStringParams.filter { it.key.isNotBlank() && it.value.isNotBlank() }
            val hasAdditionalOAuthParams = filteredHeaderParams.isNotEmpty()
                    || filteredBodyParams.isNotEmpty()
                    || filteredQueryStringParams.isNotEmpty()

            val request = when (targetMode) {
                TargetMode.ARN -> AddConsumerTarget(
                    arn = targetArn!!,
                    roleArn = targetRole,
                    rule = "INHERIT",
                    targetType = targetType,
                )
                TargetMode.API_DESTINATION -> {
                    val apiDestInput = when (authType) {
                        ApiDestinationAuthType.Basic -> ApiDestinationTargetInput(
                            invocationUrl = invocationUrl!!,
                            region = region,
                            httpMethod = httpMethod,
                            authType = authType,
                            basicCredentials = ApiDestinationBasicCredentials(
                                username = basicUsername!!,
                                password = basicPassword!!
                            )
                        )
                        ApiDestinationAuthType.ApiKey -> ApiDestinationTargetInput(
                            invocationUrl = invocationUrl!!,
                            region = region,
                            httpMethod = httpMethod,
                            authType = authType,
                            apiKeyCredentials = ApiDestinationApiKeyCredentials(
                                apiKeyName = apiKeyName!!,
                                apiKeyValue = apiKeyValue!!
                            )
                        )
                        ApiDestinationAuthType.OAuthClientCredentials -> {
                            ApiDestinationTargetInput(
                                invocationUrl = invocationUrl!!,
                                region = region,
                                httpMethod = httpMethod,
                                authType = authType,
                                oauthClientCredentials = ApiDestinationOAuthClientCredentials(
                                    clientId = oauthClientId!!,
                                    clientSecret = oauthClientSecret!!,
                                    tokenUrl = oauthTokenUrl!!,
                                    scopes = oauthScopes?.split(",")
                                        ?.map { it.trim() }
                                        ?.filter { it.isNotBlank() }
                                        ?.takeIf { it.isNotEmpty() },
                                    additionalOAuthHttpParameters = if (hasAdditionalOAuthParams)
                                        ApiDestinationOAuthHttpParameters(
                                            headerParameters = filteredHeaderParams.takeIf { it.isNotEmpty() },
                                            bodyParameters = filteredBodyParams.takeIf { it.isNotEmpty() },
                                            queryStringParameters = filteredQueryStringParams.takeIf { it.isNotEmpty() }
                                        ) else null
                                )
                            )
                        }
                        else -> error("Unsupported auth type: $authType")
                    }
                    AddConsumerTarget(
                        targetType = targetType,
                        apiDestination = apiDestInput,
                        rule = "INHERIT",
                    )
                }
            }

            aggregator.clientApi.createConsumerTarget(consumer.id, request)
            val newConsumer = aggregator.clientApi.getConsumer(consumer.id)
            onComplete(newConsumer)
        } catch (e: Throwable) {
            validationError = targetWriteFailure("add", e)
        }
    }

    dialogOverlay {
        title = "Add Target"

        // Computed in the attrs body; dialogOverlay picks up the change and re-renders the footer
        // (UCA-602), so the Add button follows the form state.
        val canSubmit = currentInputError() == null

        onClose {
            onComplete(null)
        }

        content {
            Div({
                style {
                    padding(16.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(16.px)
                }
            }) {
                // Target type (Events/Backfill) — applies to both modes
                dialogField("Type", "The type of the target") {
                    simpleSelect(targetType, listOf(TargetType.Events, TargetType.Backfill).map {
                        it.name to it
                    }) {
                        targetType = it
                    }
                }

                // Target mode selector (ARN vs API Destination)
                dialogField("Target Mode", "Choose how to deliver events to this consumer") {
                    radioGroup(targetMode.name) {
                        TargetMode.entries.forEach { mode ->
                            option(mode.label, mode.name, mode.description)
                        }
                        onChange { value ->
                            targetMode = TargetMode.valueOf(value)
                        }
                    }
                }

                // --- ARN mode fields ---
                if (targetMode == TargetMode.ARN) {
                    dialogField("Target ARN", "The ARN of the target (EventBridge bus, Kinesis stream, etc.)") {
                        simpleTextField(targetArn) { targetArn = it }
                    }
                    dialogField("Target Role", "The role to be used to publish to this target") {
                        simpleTextField(targetRole) { targetRole = it }
                    }
                }

                // --- API Destination mode fields ---
                if (targetMode == TargetMode.API_DESTINATION) {
                    dialogField("Invocation URL", "The HTTPS URL that events will be sent to") {
                        simpleTextField(invocationUrl) { invocationUrl = it }
                    }

                    dialogField("Region", "The AWS region for the API Destination") {
                        simpleSelect(region, listOf(
                            "us-east-1" to "us-east-1",
                            "us-east-2" to "us-east-2",
                            "us-west-2" to "us-west-2"
                        )) { region = it }
                    }

                    dialogField("HTTP Method", "The HTTP method to use when invoking the URL") {
                        simpleSelect(httpMethod, ApiDestinationHttpMethod.entries.map {
                            it.name to it
                        }) { httpMethod = it }
                    }

                    dialogField("Auth Type", "The authentication method for the endpoint") {
                        simpleSelect(authType, listOf(
                            "Basic" to ApiDestinationAuthType.Basic,
                            "API Key" to ApiDestinationAuthType.ApiKey,
                            "OAuth Client Credentials" to ApiDestinationAuthType.OAuthClientCredentials,
                        )) { authType = it }
                    }

                    // --- Conditional credential fields based on auth type ---
                    when (authType) {
                        ApiDestinationAuthType.Basic -> {
                            dialogField("Username", "The username for Basic authentication") {
                                simpleTextField(basicUsername) { basicUsername = it }
                            }
                            dialogField("Password", "The password for Basic authentication") {
                                simplePasswordField(basicPassword) { basicPassword = it }
                            }
                        }
                        ApiDestinationAuthType.ApiKey -> {
                            dialogField("API Key Name", "The name of the API key header (e.g., x-api-key)") {
                                simpleTextField(apiKeyName) { apiKeyName = it }
                            }
                            dialogField("API Key Value", "The secret value for the API key") {
                                simplePasswordField(apiKeyValue) { apiKeyValue = it }
                            }
                        }
                        ApiDestinationAuthType.OAuthClientCredentials -> {
                            dialogField("Client ID", "The OAuth client ID") {
                                simpleTextField(oauthClientId) { oauthClientId = it }
                            }
                            dialogField("Client Secret", "The OAuth client secret") {
                                simplePasswordField(oauthClientSecret) { oauthClientSecret = it }
                            }
                            dialogField("Token URL", "The OAuth token endpoint URL") {
                                simpleTextField(oauthTokenUrl) { oauthTokenUrl = it }
                            }
                            dialogField("Scopes", "Comma-separated list of OAuth scopes (optional)") {
                                simpleTextField(oauthScopes) { oauthScopes = it }
                            }

                            // Additional OAuth HTTP parameters (optional, sent during token handshake)
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Column)
                                    gap(8.px)
                                    marginTop(4.px)
                                }
                            }) {
                                Div({
                                    style {
                                        fontSize(13.px)
                                        color(SXMUI.subtleTextColor.value())
                                        marginBottom(4.px)
                                    }
                                }) {
                                    Text("Additional parameters sent during the OAuth token handshake (optional)")
                                }
                                OAuthHttpParameterEditor(
                                    label = "Header Parameters",
                                    parameters = oauthHeaderParams,
                                    onUpdate = { oauthHeaderParams = it }
                                )
                                OAuthHttpParameterEditor(
                                    label = "Body Parameters",
                                    parameters = oauthBodyParams,
                                    onUpdate = { oauthBodyParams = it }
                                )
                                OAuthHttpParameterEditor(
                                    label = "Query String Parameters",
                                    parameters = oauthQueryStringParams,
                                    onUpdate = { oauthQueryStringParams = it }
                                )
                            }
                        }
                        else -> {}
                    }
                }

                // Inline validation error message displayed within the overlay
                validationError?.let { error ->
                    messageBox(error, MessageType.ALERT)
                }
            }
        }

        action {
            title = "Add"
            primary = true
            showProgressOnAction = true
            enabled = canSubmit
            this.action {
                addTarget()
            }
        }
    }
}

/**
 * The message for a failed add or remove. The API answers 403 when AVP denies the change and 503 when
 * it could not decide (UCA-1240); both are told apart from other errors so a stale ALLOW in the
 * console reads as a permission problem rather than a bug.
 */
internal fun targetWriteFailure(operation: String, error: Throwable): String =
    when ((error as? SmithyError)?.httpStatusCode) {
        403 -> "You do not have permission to $operation this target."
        503 -> "Permissions could not be checked right now. Try again shortly."
        else -> "Unable to $operation target: ${error.message ?: "unknown error"}"
    }

